import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Reaction } from '@moleculeone/types';

import { getReactionMapping } from '../utils/conversion';

const MAPPING_CACHE: Record<string, Pick<Reaction, 'productMap' | 'substratesMap'>> = {};

type UseMapReactionOptions = {
  enabled?: boolean;
  reaction: Reaction;
  delay?: number;
};

async function buildMapping(
  reaction: Reaction,
  { delay = 0, abortSignal = null }: { delay: number; abortSignal: AbortSignal | null },
): Promise<Pick<Reaction, 'productMap' | 'substratesMap'>> {
  const { product, substrates, productMap, substratesMap } = reaction;

  if (Array.isArray(productMap) && Array.isArray(substratesMap)) {
    return {
      productMap,
      substratesMap,
    };
  }

  const reactionSmiles = `${substrates.join('.')}>>${product}`;

  return new Promise((resolve, reject) => {
    if (MAPPING_CACHE[reactionSmiles]) {
      resolve(MAPPING_CACHE[reactionSmiles]);
    }

    setTimeout(async () => {
      if (abortSignal) {
        if (abortSignal.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
        }

        abortSignal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')));
      }

      try {
        const result = await getReactionMapping({ product, substrates });

        MAPPING_CACHE[reactionSmiles] = result;
        resolve(result);
      } catch (error) {
        console.log(error);
      }
    }, delay);
  });
}

export function useMapReaction(options: UseMapReactionOptions) {
  const [reactionWithMapping, setReactionWithMapping] = useState<Reaction>();
  const [loading, setLoading] = useState(false);

  const { enabled = true, reaction, delay = 0 } = options;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const abortController = new AbortController();
    const abortSignal = abortController.signal;

    async function run() {
      try {
        setLoading(true);
        const mapping = await buildMapping(reaction, { delay, abortSignal });

        if (!abortSignal.aborted) {
          setReactionWithMapping({ ...reaction, ...mapping });
        }
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.log(error);
        }
      } finally {
        setLoading(false);
      }
    }

    run();

    return () => abortController.abort();
  }, [enabled, reaction?.product, JSON.stringify(reaction?.substrates)]);

  return { reactionWithMapping, loading };
}
